import React, { SyntheticEvent, useCallback, useEffect, useMemo } from "react";
import { Button, Grid, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import Modal from "components/shared/Modal";
import { ModalProps } from "store/types/ComponentProps";
import { GenerateAssessmentReportFormValues } from "store/types/FormValues";
import { NCCER_NUMBER_LIST_REGEXP, NCCER_NUMBER_REGEXP, defaultFormProps, getValidationPropsFromError } from "util/Form";

interface GenerateAssessmentReportModalProps extends ModalProps {
  onSubmit: (generateAssessmentReportValues: GenerateAssessmentReportFormValues) => void;
  title: string;
  subTitle: string;
  contentTypeId: number;
  formats: string[]
}

const defaultValues: GenerateAssessmentReportFormValues = {
  nccerNumber: "",
  format: "pdf"
};

const GenerateAssessmentReportModal: React.FunctionComponent<GenerateAssessmentReportModalProps> = ({
  title,
  subTitle,
  contentTypeId,
  open,
  onSubmit,
  onClose,
  loading,
  formats
}) => {
  const methods = useForm<GenerateAssessmentReportFormValues>({
    ...defaultFormProps,
    defaultValues,
  });
  const { control, formState, handleSubmit, reset } = methods;

  const submitButtonDisabled: boolean = useMemo(
    () => !formState.isValid || !formState.isDirty || loading!,
    [formState.isValid, formState.isDirty, loading]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [title, contentTypeId, reset]);

  const handleUpdateMethod = useCallback(
    (formValues: GenerateAssessmentReportFormValues) => {
      onSubmit(formValues);
    },
    [onSubmit]
  );

  const handleFormSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      handleSubmit((data: GenerateAssessmentReportFormValues) => handleUpdateMethod(data))();
    },
    [handleSubmit, handleUpdateMethod]
  );

  return (
    <Modal
      maxWidth={"md"}
      loading={loading}
      open={open}
      title={title}
      subTitleText={subTitle}
      headerBodyDivider={true}
      onClose={onClose}
      actions={
        <>
          <Button color={"primary"} variant={"outlined"} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={"primary"}
            variant={"contained"}
            type={"submit"}
            onClick={handleFormSubmit}
            disabled={submitButtonDisabled}
          >
            Generate Report
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Grid container spacing={4}>
          {contentTypeId === 31 ? (
            <>
              <Grid item xs={12}>
                <Typography variant={"h6"}>NCCER Numbers</Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      {...getValidationPropsFromError(error)}
                      inputRef={field.ref}
                      placeholder={"123456,9536421"}
                      fullWidth={true}
                    //displayEmpty={true}
                    />
                  )}
                  name={"nccerNumber"}
                  control={control}
                  rules={{
                    pattern: { value: NCCER_NUMBER_LIST_REGEXP, message: "NCCER Number should contain numbers, spaces, and commas only." },
                  }}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant={"h6"}>NCCER Number</Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      {...getValidationPropsFromError(error)}
                      inputRef={field.ref}
                      placeholder={"123456"}
                      fullWidth={true}
                    //displayEmpty={true}
                    />
                  )}
                  name={"nccerNumber"}
                  control={control}
                  rules={{
                    pattern: { value: NCCER_NUMBER_REGEXP, message: "NCCER Number should contain numbers only." },
                  }}
                />
              </Grid> 
            </>
          )}
          {formats?.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant={"h6"}>File Format</Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      {...getValidationPropsFromError(error)}
                      inputRef={field.ref}
                      fullWidth={true}
                      displayEmpty={false}
                    >
                      {formats.map((value) => (
                        <MenuItem key={value} value={value}>{value == 'csv' ? value.toUpperCase() + ' (Excel)' : value.toUpperCase()}</MenuItem>
                        ))}
                    </Select>
                  )}
                  name={'format'}
                  control={control}
                />
              </Grid>
            </>
          )}
        </Grid>
      </FormProvider>
    </Modal>
  );
};
export default GenerateAssessmentReportModal;
